import { useState } from 'react';
import { useAuditLogs } from '../api';
import { useScope } from '../../store/scope';
import { PageLoader, ErrorState, EmptyState, Pagination } from '../../components/ui';
import { dateTime, num } from '../../lib/format';
import type { AuditLogEntry } from '../../lib/types';

const ACTIONS = [
  'create',
  'update',
  'delete',
  'status_change',
  'login',
  'login_failed',
  'import',
  'image_upload',
  'image_delete',
  'price_change',
  'assign',
  'unassign',
  'move',
  'qr_generate',
  'qr_regenerate',
  'booking_status_change',
];

const ACTION_TONE: Record<string, string> = {
  create: 'bg-emerald-100 text-emerald-700',
  assign: 'bg-emerald-100 text-emerald-700',
  delete: 'bg-red-100 text-red-700',
  unassign: 'bg-red-100 text-red-700',
  login_failed: 'bg-red-100 text-red-700',
  update: 'bg-blue-100 text-blue-700',
  status_change: 'bg-amber-100 text-amber-700',
  booking_status_change: 'bg-amber-100 text-amber-700',
  price_change: 'bg-violet-100 text-violet-700',
  move: 'bg-violet-100 text-violet-700',
};

export function AuditLogsPage() {
  const { participationId } = useScope();
  const [page, setPage] = useState(1);
  const [action, setAction] = useState('');
  const [entity, setEntity] = useState('');
  const [search, setSearch] = useState('');
  const [scopeToExpo, setScopeToExpo] = useState(false);
  const [open, setOpen] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useAuditLogs({
    page,
    limit: 30,
    action: action || undefined,
    entity: entity || undefined,
    search: search || undefined,
    participationId: scopeToExpo && participationId ? participationId : undefined,
  });

  const rows = data?.data ?? [];

  const reset = () => setPage(1);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-ink-900">Audit Logs</h1>
        <p className="text-sm text-ink-400">
          {data?.meta ? `${num(data.meta.total)} entries` : ' '} · every admin action, newest first
        </p>
      </div>

      <div className="card flex flex-wrap items-center gap-2 p-3">
        <input
          className="input max-w-xs"
          placeholder="User / label / entity…"
          value={search}
          onChange={(e) => {
            reset();
            setSearch(e.target.value);
          }}
        />
        <select
          className="input w-auto"
          value={action}
          onChange={(e) => {
            reset();
            setAction(e.target.value);
          }}
        >
          <option value="">All actions</option>
          {ACTIONS.map((a) => (
            <option key={a} value={a}>
              {a.replace(/_/g, ' ')}
            </option>
          ))}
        </select>
        <input
          className="input w-40"
          placeholder="Entity (e.g. Rack)"
          value={entity}
          onChange={(e) => {
            reset();
            setEntity(e.target.value);
          }}
        />
        {participationId && (
          <label className="flex items-center gap-2 text-sm text-ink-600">
            <input
              type="checkbox"
              checked={scopeToExpo}
              onChange={(e) => {
                reset();
                setScopeToExpo(e.target.checked);
              }}
            />
            This expo only
          </label>
        )}
      </div>

      {isLoading ? (
        <PageLoader />
      ) : error ? (
        <ErrorState message={(error as Error).message} onRetry={() => refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState title="No matching audit entries" hint="Try clearing the filters." />
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="table-head">
                  <th className="px-3 py-2">When</th>
                  <th className="px-3 py-2">User</th>
                  <th className="px-3 py-2">Action</th>
                  <th className="px-3 py-2">Entity</th>
                  <th className="px-3 py-2">Target</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-50">
                {rows.map((r) => (
                  <AuditRow key={r._id} row={r} open={open === r._id} onToggle={() => setOpen(open === r._id ? null : r._id)} />
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-3">
            <Pagination page={page} pages={data?.meta?.pages ?? 1} onPage={setPage} />
          </div>
        </div>
      )}
    </div>
  );
}

function AuditRow({ row, open, onToggle }: { row: AuditLogEntry; open: boolean; onToggle: () => void }) {
  const hasDetail = row.oldValue != null || row.newValue != null || row.meta != null;
  return (
    <>
      <tr className="hover:bg-ink-50/60">
        <td className="whitespace-nowrap px-3 py-2 text-xs text-ink-400">{dateTime(row.timestamp)}</td>
        <td className="px-3 py-2">{row.userName}</td>
        <td className="px-3 py-2">
          <span className={`badge ${ACTION_TONE[row.action] ?? 'bg-ink-100 text-ink-600'}`}>
            {row.action.replace(/_/g, ' ')}
          </span>
        </td>
        <td className="px-3 py-2 text-ink-600">{row.entity}</td>
        <td className="px-3 py-2">
          <span className="text-ink-800">{row.entityLabel || '—'}</span>
          {row.ip && <span className="ml-2 font-mono text-xs text-ink-300">{row.ip}</span>}
        </td>
        <td className="px-3 py-2 text-right">
          {hasDetail && (
            <button className="btn-ghost px-2 py-1 text-xs" onClick={onToggle}>
              {open ? 'Hide' : 'Details'}
            </button>
          )}
        </td>
      </tr>
      {open && hasDetail && (
        <tr className="bg-ink-50/40">
          <td colSpan={6} className="px-3 py-3">
            <div className="grid gap-3 md:grid-cols-3">
              <JsonBlock label="Before" value={row.oldValue} />
              <JsonBlock label="After" value={row.newValue} />
              <JsonBlock label="Meta" value={row.meta} />
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function JsonBlock({ label, value }: { label: string; value: unknown }) {
  if (value == null) return null;
  return (
    <div>
      <p className="label mb-1">{label}</p>
      <pre className="max-h-60 overflow-auto rounded-lg bg-ink-900 p-3 text-xs text-ink-100">
        {JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
}
